import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, ArrowRight, Tag, X, Check, Loader2, Sparkles, Trophy, Star, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useCart, useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const { cart, removeFromCart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [firstPurchase, setFirstPurchase] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [pointsRedeemed, setPointsRedeemed] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [showShipping, setShowShipping] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    first_name: "", last_name: "", address1: "", address2: "",
    city: "", state: "", zip: "", country: "US", email: "", phone: ""
  });
  const [shippingComplete, setShippingComplete] = useState(false);

  const hasMerch = cart.items.some(i => i.item_type === "merch");

  const validateShipping = () => {
    const req = ["first_name","last_name","address1","city","state","zip","email"];
    return req.every(f => shippingAddress[f]?.trim());
  };

  // Check first purchase & loyalty
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    axios.get(`${API}/promo/first-purchase`, { headers, withCredentials: true })
      .then(res => { if (res.data.eligible) setFirstPurchase(res.data); }).catch(() => {});
    
    axios.get(`${API}/loyalty/me`, { headers, withCredentials: true })
      .then(res => setLoyalty(res.data)).catch(() => {});
    
    // Check for pending redemption
    axios.get(`${API}/loyalty/redeem/pending`, { headers, withCredentials: true })
      .then(res => {
        if (res.data.has_pending) {
          setPointsRedeemed(true);
          setPointsToRedeem(res.data.points_redeemed);
        }
      }).catch(() => {});
  }, [user]);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/promo/validate`,
        { code: promoCode, subtotal: cart.total },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      setPromoApplied(response.data);
      toast.success(`Promo code applied! You save $${response.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
  };

  const handleRedeemPoints = async () => {
    if (pointsToRedeem <= 0) return;
    setRedeemLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/loyalty/redeem`, { points: pointsToRedeem }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setPointsRedeemed(true);
      toast.success(`Redeemed ${pointsToRedeem} points for $${res.data.discount_value.toFixed(2)} off!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem points");
    } finally { setRedeemLoading(false); }
  };

  const handleCancelRedeem = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/loyalty/redeem`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setPointsRedeemed(false);
      setPointsToRedeem(0);
      // Refresh loyalty
      const loyaltyRes = await axios.get(`${API}/loyalty/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setLoyalty(loyaltyRes.data);
      toast.success("Points redemption cancelled");
    } catch (e) { toast.error("Failed to cancel"); }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Please login to checkout");
      return;
    }
    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (hasMerch && !shippingComplete) {
      setShowShipping(true);
      toast.error("Please enter your shipping address for merchandise");
      return;
    }
    setCheckingOut(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/payments/checkout/stripe`,
        {
          origin_url: window.location.origin,
          payment_method: "stripe",
          promo_code: promoApplied ? promoApplied.code : null,
          shipping_address: hasMerch ? shippingAddress : null
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  const firstPurchaseDiscount = (!promoApplied && firstPurchase && cart.total >= firstPurchase.min_order_amount)
    ? Math.round(cart.total * (firstPurchase.discount_value / 100) * 100) / 100
    : 0;
  const pointsDiscount = pointsRedeemed ? Math.round((pointsToRedeem / (loyalty?.points_to_dollar || 50)) * 100) / 100 : 0;
  const finalTotal = Math.max(0.50, Math.round(((promoApplied 
    ? promoApplied.new_total 
    : (cart.total - firstPurchaseDiscount)) - pointsDiscount) * 100) / 100);

  if (!user) {
    return (
      <div className="min-h-screen py-10 px-6" data-testid="cart-page">
        <div className="max-w-4xl mx-auto text-center py-20">
          <ShoppingCart className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="font-heading text-3xl uppercase mb-4">Your Cart</h1>
          <p className="text-zinc-400 mb-6">Please login to view your cart</p>
          <Button onClick={() => navigate("/")} className="bg-neon-cyan text-black">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen py-10 px-6" data-testid="cart-page">
        <div className="max-w-4xl mx-auto text-center py-20">
          <ShoppingCart className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="font-heading text-3xl uppercase mb-4">Your Cart is Empty</h1>
          <p className="text-zinc-400 mb-6">Browse our beats and merch to find something you like</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate("/beats")} className="bg-neon-cyan text-black">
              Browse Beats
            </Button>
            <Button onClick={() => navigate("/merch")} variant="outline" className="border-neon-magenta text-neon-magenta">
              Browse Merch
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-6" data-testid="cart-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 mb-4">
              Shopping Cart
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight">
              Your Cart ({cart.items.length} items)
            </h1>
          </div>
          <Button
            variant="ghost"
            onClick={clearCart}
            className="text-zinc-400 hover:text-red-500"
            data-testid="clear-cart"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>

        {/* Cart Items */}
        <div className="space-y-4 mb-10">
          {cart.items.map((item, idx) => (
            <div
              key={`${item.item_id}-${item.license_type}-${idx}`}
              className="glass p-6 flex gap-6"
              data-testid={`cart-item-${item.item_id}`}
            >
              {item.image && (
                <div className="w-24 h-24 flex-shrink-0 bg-zinc-800 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-lg mb-2">{item.name}</h3>
                <div className="flex flex-wrap gap-2 mb-2">
                  {item.license_type && (
                    <Badge variant="secondary" className="uppercase text-xs">
                      {item.license_type} License
                    </Badge>
                  )}
                  {item.size && (
                    <Badge variant="outline" className="text-xs border-white/20">
                      Size: {item.size}
                    </Badge>
                  )}
                  {item.color && (
                    <Badge variant="outline" className="text-xs border-white/20">
                      Color: {item.color}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-zinc-400">Qty: {item.quantity}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <p className="font-mono text-xl text-neon-cyan font-bold">
                  ${item.price?.toFixed(2)}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFromCart(item.item_id, item.license_type)}
                  className="text-zinc-400 hover:text-red-500"
                  data-testid={`remove-item-${item.item_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="glass p-6 md:p-8">
          <h2 className="font-heading text-xl uppercase tracking-wide mb-6">Order Summary</h2>
          
          {/* Promo Code Input */}
          <div className="mb-6" data-testid="promo-code-section">
            {promoApplied ? (
              <div className="flex items-center justify-between p-4 bg-neon-green/10 border border-neon-green/30">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-green" />
                  <div>
                    <span className="font-mono text-neon-green font-bold">{promoApplied.code}</span>
                    <span className="text-zinc-400 text-sm ml-2">
                      {promoApplied.discount_type === "percentage"
                        ? `${promoApplied.discount_value}% off`
                        : `$${promoApplied.discount_value.toFixed(2)} off`}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemovePromo}
                  className="text-zinc-400 hover:text-red-500"
                  data-testid="remove-promo-button"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="PROMO CODE"
                    className="bg-black/50 border-white/10 pl-10 font-mono uppercase tracking-widest"
                    onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
                    data-testid="promo-code-input"
                  />
                </div>
                <Button
                  onClick={handleApplyPromo}
                  disabled={promoLoading || !promoCode.trim()}
                  variant="outline"
                  className="border-neon-cyan text-neon-cyan hover:bg-neon-cyan/10 px-6"
                  data-testid="apply-promo-button"
                >
                  {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
            )}
          </div>

          {/* Points Redemption */}
          {loyalty && loyalty.current_points > 0 && !pointsRedeemed && (
            <div className="mb-6 p-4 bg-neon-yellow/5 border border-neon-yellow/20 rounded-sm" data-testid="points-redeem-section">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-neon-yellow" />
                <span className="text-sm font-medium text-neon-yellow">Use Reward Points</span>
                <Badge className="bg-neon-yellow/20 text-neon-yellow border-neon-yellow/50 text-xs ml-auto">
                  {loyalty.current_points} pts available
                </Badge>
              </div>
              <div className="flex items-center gap-3">
                <Slider
                  value={[pointsToRedeem]}
                  onValueChange={([v]) => setPointsToRedeem(v)}
                  min={0}
                  max={Math.min(loyalty.current_points, Math.floor(cart.total * (loyalty.points_to_dollar || 50)))}
                  step={10}
                  className="flex-1"
                  data-testid="points-slider"
                />
                <span className="text-sm font-mono text-neon-yellow w-20 text-right">
                  {pointsToRedeem} pts
                </span>
              </div>
              {pointsToRedeem > 0 && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-zinc-500">
                    = ${(pointsToRedeem / (loyalty.points_to_dollar || 50)).toFixed(2)} discount
                  </span>
                  <Button
                    size="sm"
                    onClick={handleRedeemPoints}
                    disabled={redeemLoading}
                    className="bg-neon-yellow text-black font-bold text-xs"
                    data-testid="redeem-points-button"
                  >
                    {redeemLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply Points"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Points Applied Badge */}
          {pointsRedeemed && (
            <div className="mb-6 flex items-center justify-between p-4 bg-neon-yellow/10 border border-neon-yellow/30" data-testid="points-applied">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-neon-yellow" />
                <span className="text-sm text-neon-yellow font-bold">{pointsToRedeem} points redeemed</span>
                <span className="text-xs text-zinc-400">(-${pointsDiscount.toFixed(2)})</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCancelRedeem} className="text-zinc-400 hover:text-red-500" data-testid="cancel-redeem-button">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          <div className="space-y-4 mb-6">
            <div className="flex justify-between text-zinc-400">
              <span>Subtotal</span>
              <span className="font-mono">${cart.total?.toFixed(2)}</span>
            </div>
            {promoApplied && (
              <div className="flex justify-between text-neon-green">
                <span>Discount ({promoApplied.code})</span>
                <span className="font-mono">-${promoApplied.discount_amount.toFixed(2)}</span>
              </div>
            )}
            {!promoApplied && firstPurchaseDiscount > 0 && (
              <div className="flex justify-between text-neon-green" data-testid="first-purchase-discount">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  First Order - 10% OFF
                </span>
                <span className="font-mono">-${firstPurchaseDiscount.toFixed(2)}</span>
              </div>
            )}
            {pointsRedeemed && pointsDiscount > 0 && (
              <div className="flex justify-between text-neon-yellow">
                <span className="flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Points Redeemed
                </span>
                <span className="font-mono">-${pointsDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-zinc-400">
              <span>Processing Fee</span>
              <span className="font-mono">$0.00</span>
            </div>
            <div className="border-t border-white/10 pt-4 flex justify-between">
              <span className="font-medium">Total</span>
              <span className="font-heading text-2xl text-neon-cyan">${finalTotal?.toFixed(2)}</span>
            </div>
          </div>

          {/* Shipping Address (merch orders) */}
          {hasMerch && (
            <div className="mb-6" data-testid="shipping-section">
              <button
                onClick={() => setShowShipping(!showShipping)}
                className="w-full flex items-center justify-between p-4 bg-zinc-900 border border-white/10 hover:border-neon-cyan/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <MapPin className={`w-4 h-4 ${shippingComplete ? "text-neon-green" : "text-neon-cyan"}`} />
                  <span className="text-sm font-medium">
                    {shippingComplete
                      ? `Shipping to: ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`
                      : "Enter Shipping Address (required for merch)"}
                  </span>
                  {shippingComplete && <Check className="w-4 h-4 text-neon-green" />}
                </div>
                {showShipping ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
              </button>

              {showShipping && (
                <div className="p-4 border border-white/10 border-t-0 bg-zinc-900/50 space-y-3" data-testid="shipping-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">First Name *</label>
                      <Input value={shippingAddress.first_name} onChange={e => setShippingAddress(s => ({...s, first_name: e.target.value}))}
                        placeholder="John" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-first-name" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">Last Name *</label>
                      <Input value={shippingAddress.last_name} onChange={e => setShippingAddress(s => ({...s, last_name: e.target.value}))}
                        placeholder="Doe" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-last-name" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Email *</label>
                    <Input value={shippingAddress.email} onChange={e => setShippingAddress(s => ({...s, email: e.target.value}))}
                      placeholder="john@example.com" type="email" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-email" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Address Line 1 *</label>
                    <Input value={shippingAddress.address1} onChange={e => setShippingAddress(s => ({...s, address1: e.target.value}))}
                      placeholder="123 Main Street" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-address1" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Address Line 2</label>
                    <Input value={shippingAddress.address2} onChange={e => setShippingAddress(s => ({...s, address2: e.target.value}))}
                      placeholder="Apt 4B (optional)" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-address2" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <label className="text-xs text-zinc-400 mb-1 block">City *</label>
                      <Input value={shippingAddress.city} onChange={e => setShippingAddress(s => ({...s, city: e.target.value}))}
                        placeholder="Los Angeles" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-city" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">State *</label>
                      <Input value={shippingAddress.state} onChange={e => setShippingAddress(s => ({...s, state: e.target.value}))}
                        placeholder="CA" maxLength={2} className="bg-black/50 border-white/10 text-sm uppercase" data-testid="shipping-state" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 mb-1 block">ZIP *</label>
                      <Input value={shippingAddress.zip} onChange={e => setShippingAddress(s => ({...s, zip: e.target.value}))}
                        placeholder="90001" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-zip" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Country</label>
                    <Select value={shippingAddress.country} onValueChange={v => setShippingAddress(s => ({...s, country: v}))}>
                      <SelectTrigger className="bg-black/50 border-white/10 text-sm" data-testid="shipping-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                        <SelectItem value="AU">Australia</SelectItem>
                        <SelectItem value="DE">Germany</SelectItem>
                        <SelectItem value="FR">France</SelectItem>
                        <SelectItem value="JP">Japan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Phone</label>
                    <Input value={shippingAddress.phone} onChange={e => setShippingAddress(s => ({...s, phone: e.target.value}))}
                      placeholder="555-123-4567" type="tel" className="bg-black/50 border-white/10 text-sm" data-testid="shipping-phone" />
                  </div>
                  <Button
                    onClick={() => {
                      if (validateShipping()) {
                        setShippingComplete(true);
                        setShowShipping(false);
                        toast.success("Shipping address saved!");
                      } else {
                        toast.error("Please fill in all required fields");
                      }
                    }}
                    className="w-full bg-neon-cyan text-black font-bold text-sm uppercase tracking-wider"
                    data-testid="save-shipping-btn"
                  >
                    Save Shipping Address
                  </Button>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={handleCheckout}
            disabled={checkingOut || (hasMerch && !shippingComplete)}
            className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest py-6 btn-clip neon-cyan hover:scale-[1.02] transition-transform disabled:opacity-50"
            data-testid="checkout-button"
          >
            {checkingOut ? (
              "Processing..."
            ) : hasMerch && !shippingComplete ? (
              <>
                <MapPin className="w-5 h-5 mr-2" />
                Enter Shipping Address First
              </>
            ) : (
              <>
                Proceed to Checkout
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          <p className="text-center text-xs text-zinc-600 mt-4">
            Secure checkout powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
